import React, { useEffect, useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { fetchSellersStats } from '../../Redux/Actions/actions';
import {
  Chart as ChartJS,
  CategoryScale, LinearScale, BarElement, ArcElement,
  Tooltip, Legend
} from 'chart.js';
import { Bar, Doughnut } from 'react-chartjs-2';
import styles from './estadisticas.module.css';

ChartJS.register(CategoryScale, LinearScale, BarElement, ArcElement, Tooltip, Legend);

const formatDate = (d) => {
  if (!d) return '—';
  const date = new Date(d);
  return `${String(date.getDate()).padStart(2, '0')}/${String(date.getMonth() + 1).padStart(2, '0')}/${date.getFullYear()}`;
};

const formatCurrency = (amount, currency = 'USD') => {
  if (!amount && amount !== 0) return '—';
  const num = Number(amount);
  if (currency === 'USD') return `US$ ${num.toLocaleString('es-AR')}`;
  return `$ ${num.toLocaleString('es-AR')}`;
};

const HelpBox = ({ children }) => {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button className={styles.helpToggle} onClick={() => setOpen(o => !o)} type="button">
        ℹ️ {open ? 'Ocultar ayuda' : '¿Qué significa esto?'}
        <span className={`${styles.helpToggleIcon} ${open ? styles.helpToggleIconOpen : ''}`}>▼</span>
      </button>
      {open && <div className={styles.helpDescription}>{children}</div>}
    </>
  );
};

const ProductividadStats = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { sellersStats, estadisticasLoading, estadisticasError } = useSelector(s => s);

  const [filters, setFilters] = useState({ startDate: '', endDate: '' });
  const [sortField, setSortField] = useState('totalSoldProperties');
  const [sortDir, setSortDir] = useState('desc');
  const [expandedSeller, setExpandedSeller] = useState(null);
  const [segmentModal, setSegmentModal] = useState(null);

  const loadData = useCallback(() => {
    const params = { includeDetails: 'true' };
    if (filters.startDate) params.startDate = filters.startDate;
    if (filters.endDate) params.endDate = filters.endDate;
    dispatch(fetchSellersStats(params));
  }, [dispatch, filters]);

  useEffect(() => { loadData(); }, []);

  if (estadisticasLoading) {
    return <div className={styles.loadingContainer}><div className={styles.spinner} /><span>Cargando ventas cerradas...</span></div>;
  }

  if (estadisticasError) {
    return <div className={styles.errorContainer}><span>⚠️ {estadisticasError}</span><button className={styles.retryButton} onClick={loadData}>Reintentar</button></div>;
  }

  if (!sellersStats) {
    return <div className={styles.emptyState}><span className={styles.emptyIcon}>📈</span><span>No hay datos disponibles</span></div>;
  }

  const summary = sellersStats.summary || {};
  const sellersWithSales = sellersStats.sellersWithSales || [];
  const sellersWithoutSales = sellersStats.sellersWithoutSales || [];
  const apiFilters = sellersStats.filters || {};

  // Sort
  // Sort helper: compute avgDaysPerProp
  const getAvgDays = (s) => {
    if (!s.createdAt || !s.totalLoadedProperties) return 0;
    return Math.floor((Date.now() - new Date(s.createdAt).getTime()) / 86400000) / s.totalLoadedProperties;
  };

  const sorted = [...sellersWithSales].sort((a, b) => {
    if (sortField === 'avgDaysPerProp') {
      const aVal = getAvgDays(a);
      const bVal = getAvgDays(b);
      return sortDir === 'desc' ? bVal - aVal : aVal - bVal;
    }
    const aVal = sortField === 'fullName' ? (a[sortField] || '') : (a[sortField] || 0);
    const bVal = sortField === 'fullName' ? (b[sortField] || '') : (b[sortField] || 0);
    if (sortField === 'fullName') return sortDir === 'desc' ? bVal.localeCompare(aVal) : aVal.localeCompare(bVal);
    return sortDir === 'desc' ? bVal - aVal : aVal - bVal;
  });

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDir(d => d === 'desc' ? 'asc' : 'desc');
    } else {
      setSortField(field);
      setSortDir('desc');
    }
  };

  const sortIcon = (field) => sortField === field ? (sortDir === 'desc' ? ' ▼' : ' ▲') : '';

  // KPI Cards
  const totalAllClosed = (summary.totalSoldProperties || 0) + (summary.totalSoldByAgencyProperties || 0) + (summary.totalUnclassifiedProperties || 0) + (summary.totalOperacionesExternas || 0);
  const kpis = [
    { label: 'Agentes activos', value: summary.activeSellersCount || 0, sub: `con ventas: ${summary.sellersWithSalesCount || 0} · sin ventas: ${summary.sellersWithoutSalesCount || 0}`, icon: '👥', color: '#2E2F8F' },
    { label: 'Props cargadas', value: summary.totalLoadedProperties || 0, sub: totalAllClosed ? `${totalAllClosed} cerradas (${((totalAllClosed / (summary.totalLoadedProperties || 1)) * 100).toFixed(0)}%)` : '', icon: '📋', color: '#3D3FAD' },
    { label: 'Vendidas por agente', value: summary.totalSoldProperties || 0, sub: totalAllClosed ? `${((summary.totalSoldProperties / totalAllClosed) * 100).toFixed(0)}% del total cerradas` : '', icon: '🏠', color: '#27AE60' },
    { label: 'Cerradas por agencia', value: summary.totalSoldByAgencyProperties || 0, sub: totalAllClosed ? `${((summary.totalSoldByAgencyProperties / totalAllClosed) * 100).toFixed(0)}% del total cerradas` : '', icon: '🏢', color: '#E67E22' },
    { label: 'Op. Externas (Punta)', value: summary.totalOperacionesExternas || 0, sub: 'punta compradora', icon: '🤝', color: '#A83E52' },
    { label: 'Sin clasificar', value: summary.totalUnclassifiedProperties || 0, sub: 'datos legacy pendientes', icon: '❓', color: '#6B6B8A' },
    { label: 'Monto total (agente)', value: formatCurrency(summary.totalSaleAmount), sub: `${summary.totalSoldProperties || 0} propiedades`, icon: '💰', color: '#1A1A3E' },
  ];

  // Donut: agentes con vs sin ventas
  const donutData = {
    labels: ['Con ventas', 'Sin ventas'],
    datasets: [{
      data: [summary.sellersWithSalesCount || 0, summary.sellersWithoutSalesCount || 0],
      backgroundColor: ['#27AE60', '#E74C3C'],
      borderWidth: 0,
      spacing: 2
    }]
  };
  const donutOpts = { responsive: true, cutout: '70%', plugins: { legend: { position: 'bottom', labels: { boxWidth: 12 } } } };

  // Donut: distribución de propiedades cerradas por categoría
  const propCatDonutData = {
    labels: ['Por agente', 'Por agencia', 'Sin clasificar', 'Op. Externas'],
    datasets: [{
      data: [summary.totalSoldProperties || 0, summary.totalSoldByAgencyProperties || 0, summary.totalUnclassifiedProperties || 0, summary.totalOperacionesExternas || 0],
      backgroundColor: ['#27AE60', '#E67E22', '#95A5A6', '#A83E52'],
      borderWidth: 0,
      spacing: 2
    }]
  };

  // Click handlers para donuts
  const agentDonutLabels = ['Con ventas', 'Sin ventas'];
  const agentDonutColors = { 'Con ventas': '#27AE60', 'Sin ventas': '#E74C3C' };

  const handleAgentDonutClick = (_, elements) => {
    if (!elements.length) return;
    const seg = agentDonutLabels[elements[0].index];
    const items = seg === 'Con ventas' ? sellersWithSales : sellersWithoutSales;
    setSegmentModal({ type: 'agents', segment: seg, items, color: agentDonutColors[seg] });
  };

  const propCatLabels = ['Por agente', 'Por agencia', 'Sin clasificar', 'Op. Externas'];
  const propCatColors = { 'Por agente': '#27AE60', 'Por agencia': '#E67E22', 'Sin clasificar': '#95A5A6', 'Op. Externas': '#A83E52' };
  const propCatKeys = { 'Por agente': 'properties', 'Por agencia': 'soldByAgencyProperties', 'Sin clasificar': 'unclassifiedClosedProperties', 'Op. Externas': 'operacionesExternas' };

  const handlePropCatDonutClick = (_, elements) => {
    if (!elements.length) return;
    const seg = propCatLabels[elements[0].index];
    const key = propCatKeys[seg];
    const items = sellersWithSales.flatMap(s => (s[key] || []).map(p => ({ ...p, sellerName: s.fullName })));
    setSegmentModal({ type: 'properties', segment: seg, items, color: propCatColors[seg] });
  };

  // Bar chart (top 10 por propiedades cargadas)
  const top10 = [...sellersWithSales].sort((a, b) => (b.totalLoadedProperties || 0) - (a.totalLoadedProperties || 0)).slice(0, 10);
  const barData = {
    labels: top10.map(s => s.fullName),
    datasets: [
      { label: 'Cargadas', data: top10.map(s => s.totalLoadedProperties || 0), backgroundColor: '#2E2F8F', borderRadius: 6 },
      { label: 'Vendidas por agente', data: top10.map(s => s.totalSoldProperties || 0), backgroundColor: '#27AE60', borderRadius: 6 },
      { label: 'Cerradas por agencia', data: top10.map(s => s.soldByAgencyCount || 0), backgroundColor: '#E67E22', borderRadius: 6 },
      { label: 'Sin clasificar', data: top10.map(s => s.unclassifiedClosedCount || 0), backgroundColor: '#95A5A6', borderRadius: 6 },
      { label: 'Op. Externas', data: top10.map(s => s.operacionesExternasCount || 0), backgroundColor: '#A83E52', borderRadius: 6 }
    ]
  };
  const barOpts = {
    indexAxis: 'y',
    responsive: true,
    plugins: { legend: { position: 'bottom' } },
    scales: {
      x: { beginAtZero: true, grid: { color: '#F0F0F8' } },
      y: { grid: { display: false } }
    },
    barPercentage: 0.7,
    categoryPercentage: 0.8
  };

  const medals = ['🥇', '🥈', '🥉'];

  return (
    <div className={styles.dashboardContainer}>
      {/* Header */}
      <div className={styles.dashboardHeader}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
          <button className={styles.backButton} onClick={() => navigate('/estadisticas')}>← Volver</button>
          <h1 className={styles.dashboardTitle}>Ventas Cerradas por Agente</h1>
        </div>
        <button className={styles.pdfButton} onClick={() => window.print()}>📄 Exportar PDF</button>
      </div>

      {/* Filtros */}
      <div className={styles.filtersBar}>
        <div className={styles.filterGroup}>
          <label className={styles.filterLabel}>Fecha inicio</label>
          <input type="date" className={styles.filterInputDate} style={{ height: 50, minHeight: 50, fontSize: 16, padding: '0 16px', width: 220, minWidth: 220, borderRadius: 10, background: '#f5f4f2', border: '1px solid #ccc9c4', boxSizing: 'border-box' }} value={filters.startDate} onChange={e => setFilters(p => ({ ...p, startDate: e.target.value }))} />
        </div>
        <div className={styles.filterGroup}>
          <label className={styles.filterLabel}>Fecha fin</label>
          <input type="date" className={styles.filterInputDate} style={{ height: 50, minHeight: 50, fontSize: 16, padding: '0 16px', width: 220, minWidth: 220, borderRadius: 10, background: '#f5f4f2', border: '1px solid #ccc9c4', boxSizing: 'border-box' }} value={filters.endDate} onChange={e => setFilters(p => ({ ...p, endDate: e.target.value }))} />
        </div>
        <div className={styles.filterButtons}>
          <button className={styles.filterButton} onClick={loadData}>Aplicar filtros</button>
          <button className={styles.clearButton} onClick={() => { setFilters({ startDate: '', endDate: '' }); dispatch(fetchSellersStats({ includeDetails: 'true' })); }}>Limpiar</button>
        </div>
      </div>

      {apiFilters.startDate && (
        <div className={styles.conversionBadge} style={{ marginBottom: 16 }}>
          📅 Mostrando ventas del {apiFilters.startDate} al {apiFilters.endDate}
        </div>
      )}

      {/* KPI Cards */}
      <div className={styles.sectionTitleRow}>
        <h3 className={styles.sectionTitle}>Indicadores Clave</h3>
        <HelpBox>
          <p>Resumen de propiedades cerradas clasificadas por tipo:</p>
          <ul>
            <li><strong>Vendidas por agente:</strong> Propiedades donde soldByAgent = true.</li>
            <li><strong>Cerradas por agencia:</strong> Propiedades donde soldByAgent = false (cerradas por agencia/otro).</li>
            <li><strong>Sin clasificar:</strong> Datos legacy sin soldByAgent definido.</li>
            <li><strong>Monto total:</strong> Suma de precios de cierre (solo ventas por agente).</li>
          </ul>
        </HelpBox>
      </div>
      <div className={`${styles.kpiGrid} ${styles.kpiGrid6}`}>
        {kpis.map((k, i) => (
          <div key={i} className={styles.kpiCard}>
            <div className={styles.kpiIcon} style={{ background: `${k.color}15` }}>{k.icon}</div>
            <span className={styles.kpiLabel}>{k.label}</span>
            <span className={styles.kpiValue}>{k.value}</span>
            {k.sub && <span className={styles.kpiSub}>{k.sub}</span>}
          </div>
        ))}
      </div>

      {/* Donut + Barras */}
      <div className={styles.chartRow}>
        <div className={styles.section}>
          <div className={styles.sectionTitleRow}>
            <h3 className={styles.sectionTitle}>Distribución de Agentes y Propiedades</h3>
            <HelpBox>
              <p>Distribución de agentes activos y propiedades cerradas por categoría:</p>
              <ul>
                <li><strong>Por agente:</strong> Vendidas directamente por el agente (soldByAgent = true).</li>
                <li><strong>Por agencia:</strong> Cerradas por la agencia u otro (soldByAgent = false).</li>
                <li><strong>Sin clasificar:</strong> Datos legacy sin categorizar.</li>
              </ul>
            </HelpBox>
          </div>
          <div style={{ display: 'flex', gap: 32, justifyContent: 'center', flexWrap: 'wrap' }}>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: 12, fontWeight: 600, color: '#6B6B8A', marginBottom: 8, textTransform: 'uppercase' }}>Agentes</div>
              <div className={styles.donutWrapper}>
                <div style={{ maxWidth: 220, position: 'relative' }}>
                  <Doughnut data={donutData} options={{ ...donutOpts, onClick: handleAgentDonutClick }} />
                  <div className={styles.donutCenter}>
                    <div className={styles.donutCenterValue}>{summary.activeSellersCount || 0}</div>
                    <div className={styles.donutCenterLabel}>Agentes</div>
                  </div>
                </div>
              </div>
            </div>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: 12, fontWeight: 600, color: '#6B6B8A', marginBottom: 8, textTransform: 'uppercase' }}>Propiedades cerradas</div>
              <div className={styles.donutWrapper}>
                <div style={{ maxWidth: 220, position: 'relative' }}>
                  <Doughnut data={propCatDonutData} options={{ ...donutOpts, onClick: handlePropCatDonutClick }} />
                  <div className={styles.donutCenter}>
                    <div className={styles.donutCenterValue}>{totalAllClosed}</div>
                    <div className={styles.donutCenterLabel}>Cerradas</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className={styles.section}>
          <div className={styles.sectionTitleRow}>
            <h3 className={styles.sectionTitle}>Top 10 Agentes — Cargadas vs Cerradas</h3>
            <HelpBox>
              <p>Gráfico comparativo de los 10 agentes con más propiedades cargadas:</p>
              <ul>
                <li><strong>Azul:</strong> Total de propiedades cargadas por el agente.</li>
                <li><strong>Verde:</strong> Vendidas por el agente (soldByAgent = true).</li>
                <li><strong>Naranja:</strong> Cerradas por agencia/otro (soldByAgent = false).</li>
                <li><strong>Gris:</strong> Sin clasificar (datos legacy).</li>
                <li><strong>Bordo:</strong> Operaciones externas (punta compradora).</li>
              </ul>
            </HelpBox>
          </div>
          <div className={styles.chartContainer}>
            {top10.length > 0 ? <Bar data={barData} options={barOpts} /> : <div className={styles.emptyState}>Sin datos</div>}
          </div>
        </div>
      </div>

      {/* Tabla de ranking */}
      <div className={styles.section}>
        <div className={styles.sectionTitleRow}>
          <h3 className={styles.sectionTitle}>Ranking de Ventas por Agente</h3>
          <HelpBox>
            <p>Tabla con todos los agentes que tienen propiedades cerradas. Haz clic en las columnas para ordenar, y en una fila para ver el detalle.</p>
            <ul>
              <li><strong>Cargadas:</strong> Total de propiedades cargadas por el agente.</li>
              <li><strong>Vendidas:</strong> Propiedades cerradas por el agente (soldByAgent = true).</li>
              <li><strong>Agencia:</strong> Cerradas por agencia/otro (soldByAgent = false).</li>
              <li><strong>Sin clasif.:</strong> Datos legacy sin clasificar.</li>
              <li><strong>Op. Ext.:</strong> Operaciones externas (punta compradora).</li>
              <li><strong>Prom. días/prop:</strong> Días desde alta del agente ÷ propiedades cargadas.</li>
            </ul>
          </HelpBox>
        </div>
        <div className={styles.tableContainer}>
          <table className={styles.dataTable}>
            <thead>
              <tr>
                <th>#</th>
                <th onClick={() => handleSort('fullName')} style={{ cursor: 'pointer' }}>Agente<span className={styles.sortIndicator}>{sortIcon('fullName')}</span></th>
                <th onClick={() => handleSort('totalLoadedProperties')} style={{ cursor: 'pointer' }}>Cargadas<span className={styles.sortIndicator}>{sortIcon('totalLoadedProperties')}</span></th>
                <th onClick={() => handleSort('totalSoldProperties')} style={{ cursor: 'pointer' }}>Vendidas<span className={styles.sortIndicator}>{sortIcon('totalSoldProperties')}</span></th>
                <th onClick={() => handleSort('soldByAgencyCount')} style={{ cursor: 'pointer' }}>Agencia<span className={styles.sortIndicator}>{sortIcon('soldByAgencyCount')}</span></th>
                <th onClick={() => handleSort('unclassifiedClosedCount')} style={{ cursor: 'pointer' }}>Sin clasif.<span className={styles.sortIndicator}>{sortIcon('unclassifiedClosedCount')}</span></th>
                <th onClick={() => handleSort('operacionesExternasCount')} style={{ cursor: 'pointer' }}>Op. Ext.<span className={styles.sortIndicator}>{sortIcon('operacionesExternasCount')}</span></th>
                <th onClick={() => handleSort('avgDaysPerProp')} style={{ cursor: 'pointer' }}>Prom. días/prop<span className={styles.sortIndicator}>{sortIcon('avgDaysPerProp')}</span></th>
              </tr>
            </thead>
            <tbody>
              {sorted.length === 0 ? (
                <tr><td colSpan={8} style={{ textAlign: 'center', padding: 24, color: '#6B6B8A' }}>Sin datos de ventas</td></tr>
              ) : sorted.map((s, i) => (
                <React.Fragment key={s.sellerId}>
                  <tr onClick={() => setExpandedSeller(expandedSeller === s.sellerId ? null : s.sellerId)} style={{ cursor: 'pointer' }}>
                    <td><span className={styles.medal}>{medals[i] || i + 1}</span></td>
                    <td style={{ fontWeight: 500 }}>{s.fullName}</td>
                    <td>{s.totalLoadedProperties || 0}</td>
                    <td style={{ fontWeight: 700 }}>{s.totalSoldProperties}</td>
                    <td>
                      {s.soldByAgencyCount ? (
                        <span style={{ background: '#FFF4E6', color: '#E67E22', padding: '2px 10px', borderRadius: 20, fontSize: 11, fontWeight: 600 }}>{s.soldByAgencyCount}</span>
                      ) : <span style={{ color: '#ccc' }}>0</span>}
                    </td>
                    <td>
                      {s.unclassifiedClosedCount ? (
                        <span style={{ background: '#EAECEE', color: '#7F8C8D', padding: '2px 10px', borderRadius: 20, fontSize: 11, fontWeight: 600 }}>{s.unclassifiedClosedCount}</span>
                      ) : <span style={{ color: '#ccc' }}>0</span>}
                    </td>
                    <td>
                      {s.operacionesExternasCount ? (
                        <span style={{ background: '#FDE8EC', color: '#A83E52', padding: '2px 10px', borderRadius: 20, fontSize: 11, fontWeight: 600 }}>{s.operacionesExternasCount}</span>
                      ) : <span style={{ color: '#ccc' }}>0</span>}
                    </td>
                    <td>
                      {(() => {
                        if (!s.createdAt || !s.totalLoadedProperties) return <span style={{ color: '#ccc' }}>—</span>;
                        const days = Math.floor((Date.now() - new Date(s.createdAt).getTime()) / 86400000);
                        const avg = (days / s.totalLoadedProperties).toFixed(1);
                        return <span style={{ background: '#E8E8F8', color: '#2E2F8F', padding: '2px 10px', borderRadius: 20, fontSize: 11, fontWeight: 600 }}>{avg} días</span>;
                      })()}
                    </td>
                  </tr>
                  {/* Detalle expandible */}
                  {expandedSeller === s.sellerId && (
                    <tr>
                      <td colSpan={8} style={{ padding: 0 }}>
                        <div className={styles.expandPanel}>
                          {/* Todas las propiedades del agente */}
                          {s.allProperties && s.allProperties.length > 0 && (
                            <>
                              <div style={{ fontSize: 13, fontWeight: 600, color: '#2E2F8F', marginBottom: 8 }}>📋 Todas las propiedades ({s.allProperties.length})</div>
                              <table className={styles.dataTable} style={{ marginBottom: 16 }}>
                                <thead>
                                  <tr>
                                    <th>ID</th><th>Título</th><th>Estado</th><th>Categoría</th><th>Precio cierre</th><th>Moneda</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {s.allProperties.map(p => (
                                    <tr key={p.id}>
                                      <td>{p.id}</td>
                                      <td style={{ fontWeight: 500 }}>{(p.title || '').slice(0, 40)}</td>
                                      <td>
                                        <span style={{ background: p.isClosed ? '#EAF8F0' : '#FFF4E6', color: p.isClosed ? '#27AE60' : '#E67E22', padding: '2px 8px', borderRadius: 20, fontSize: 10, fontWeight: 600 }}>
                                          {p.isClosed ? 'Cerrada' : 'Activa'}
                                        </span>
                                      </td>
                                      <td>
                                        <span style={{ background: p.soldByAgentCategory === 'agent' ? '#EAF8F0' : p.soldByAgentCategory === 'agency' ? '#FFF4E6' : '#EAECEE', color: p.soldByAgentCategory === 'agent' ? '#27AE60' : p.soldByAgentCategory === 'agency' ? '#E67E22' : '#7F8C8D', padding: '2px 8px', borderRadius: 20, fontSize: 10, fontWeight: 600 }}>
                                          {p.soldByAgentCategory === 'agent' ? 'Agente' : p.soldByAgentCategory === 'agency' ? 'Agencia' : p.soldByAgentCategory === 'unclassified' ? 'Sin clasif.' : '—'}
                                        </span>
                                      </td>
                                      <td style={{ fontWeight: 600 }}>{p.precioCierre ? formatCurrency(p.precioCierre, p.currencyCierre) : '—'}</td>
                                      <td>{p.currencyCierre || '—'}</td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </>
                          )}
                          {/* Vendidas por agente */}
                          {s.properties && s.properties.length > 0 && (
                            <>
                              <div style={{ fontSize: 13, fontWeight: 600, color: '#27AE60', marginBottom: 8 }}>🏠 Vendidas por agente ({s.properties.length})</div>
                              <table className={styles.dataTable} style={{ marginBottom: 16 }}>
                                <thead>
                                  <tr>
                                    <th>ID</th><th>Título</th><th>Fecha cierre</th><th>Precio cierre</th><th>Moneda</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {s.properties.map(p => (
                                    <tr key={p.id}>
                                      <td>{p.id}</td>
                                      <td style={{ fontWeight: 500 }}>{(p.title || '').slice(0, 40)}</td>
                                      <td>{formatDate(p.closedAt)}</td>
                                      <td style={{ fontWeight: 600 }}>{formatCurrency(p.precioCierre, p.currencyCierre)}</td>
                                      <td>{p.currencyCierre || '—'}</td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </>
                          )}
                          {/* Cerradas por agencia */}
                          {s.soldByAgencyProperties && s.soldByAgencyProperties.length > 0 && (
                            <>
                              <div style={{ fontSize: 13, fontWeight: 600, color: '#E67E22', marginBottom: 8 }}>🏢 Cerradas por agencia ({s.soldByAgencyProperties.length})</div>
                              <table className={styles.dataTable} style={{ marginBottom: 16 }}>
                                <thead>
                                  <tr>
                                    <th>ID</th><th>Título</th><th>Fecha cierre</th><th>Precio cierre</th><th>Moneda</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {s.soldByAgencyProperties.map(p => (
                                    <tr key={p.id}>
                                      <td>{p.id}</td>
                                      <td style={{ fontWeight: 500 }}>{(p.title || '').slice(0, 40)}</td>
                                      <td>{formatDate(p.closedAt)}</td>
                                      <td style={{ fontWeight: 600 }}>{formatCurrency(p.precioCierre, p.currencyCierre)}</td>
                                      <td>{p.currencyCierre || '—'}</td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </>
                          )}
                          {/* Sin clasificar */}
                          {s.unclassifiedClosedProperties && s.unclassifiedClosedProperties.length > 0 && (
                            <>
                              <div style={{ fontSize: 13, fontWeight: 600, color: '#95A5A6', marginBottom: 8 }}>❓ Sin clasificar ({s.unclassifiedClosedProperties.length})</div>
                              <table className={styles.dataTable}>
                                <thead>
                                  <tr>
                                    <th>ID</th><th>Título</th><th>Fecha cierre</th><th>Precio cierre</th><th>Moneda</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {s.unclassifiedClosedProperties.map(p => (
                                    <tr key={p.id}>
                                      <td>{p.id}</td>
                                      <td style={{ fontWeight: 500 }}>{(p.title || '').slice(0, 40)}</td>
                                      <td>{formatDate(p.closedAt)}</td>
                                      <td style={{ fontWeight: 600 }}>{formatCurrency(p.precioCierre, p.currencyCierre)}</td>
                                      <td>{p.currencyCierre || '—'}</td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </>
                          )}
                          {/* Operaciones Externas (Punta Compradora) */}
                          {s.operacionesExternas && s.operacionesExternas.length > 0 && (
                            <>
                              <div style={{ fontSize: 13, fontWeight: 600, color: '#A83E52', marginBottom: 8 }}>🤝 Operaciones Externas — Punta Compradora ({s.operacionesExternas.length})</div>
                              <table className={styles.dataTable} style={{ marginBottom: 16 }}>
                                <thead>
                                  <tr>
                                    <th>ID</th><th>Título</th><th>Dirección</th><th>Inmobiliaria</th><th>Fecha cierre</th><th>Precio cierre</th><th>Moneda</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {s.operacionesExternas.map(op => (
                                    <tr key={op.id}>
                                      <td>{op.id}</td>
                                      <td style={{ fontWeight: 500 }}>{(op.titulo || '').slice(0, 40)}</td>
                                      <td>{(op.direccion || '').slice(0, 30)}</td>
                                      <td>{op.inmobiliariaExterna || '—'}</td>
                                      <td>{formatDate(op.closedAt || op.fechaCierre)}</td>
                                      <td style={{ fontWeight: 600 }}>{formatCurrency(op.precioCierre, op.currencyCierre)}</td>
                                      <td>{op.currencyCierre || '—'}</td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </>
                          )}
                          {(!s.properties || s.properties.length === 0) && (!s.soldByAgencyProperties || s.soldByAgencyProperties.length === 0) && (!s.unclassifiedClosedProperties || s.unclassifiedClosedProperties.length === 0) && (!s.operacionesExternas || s.operacionesExternas.length === 0) && (
                            <div style={{ textAlign: 'center', color: '#6B6B8A', padding: 16 }}>Sin detalle de propiedades disponible</div>
                          )}
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Agentes sin ventas */}
      {sellersWithoutSales.length > 0 && (
        <div className={styles.section}>
          <div className={styles.sectionTitleRow}>
            <h3 className={styles.sectionTitle}>
              Agentes sin ventas cerradas
              <span className={styles.badge} style={{ background: '#FEF0F0', color: '#C0392B', marginLeft: 8 }}>{sellersWithoutSales.length}</span>
            </h3>
            <HelpBox>
              <p>Lista de agentes activos que no tienen ninguna propiedad cerrada (ni por agente, ni por agencia, ni sin clasificar) en el período seleccionado.</p>
              <p>💡 Los agentes con solo operaciones externas (punta compradora) ahora aparecen en la sección de agentes CON ventas.</p>
              <p>💡 Considerá reuniones de seguimiento o acompañamiento para estos agentes.</p>
            </HelpBox>
          </div>
          <div className={styles.noPropsGrid}>
            {sellersWithoutSales.map(s => (
              <div key={s.sellerId} className={styles.noPropCard}>
                <p style={{ fontWeight: 600, color: '#1A1A3E' }}>{s.fullName || `${s.name} ${s.lastName}`}</p>
                {s.mail && <p>{s.mail}</p>}
              </div>
            ))}
          </div>
        </div>
      )}
      {/* Modal de detalle de segmento */}
      {segmentModal && (
        <div className={styles.modalOverlay} onClick={() => setSegmentModal(null)}>
          <div className={styles.segmentModalContent} onClick={e => e.stopPropagation()}>
            <div className={styles.segmentModalHeader}>
              <div className={styles.segmentModalTitle}>
                <span className={styles.segmentModalDot} style={{ background: segmentModal.color }} />
                {segmentModal.type === 'properties' ? 'Propiedades' : 'Agentes'} — {segmentModal.segment}
              </div>
              <button className={styles.segmentModalClose} onClick={() => setSegmentModal(null)}>✕</button>
            </div>
            <div className={styles.segmentModalCount}>
              {segmentModal.items.length} {segmentModal.type === 'properties' ? 'propiedades' : 'agentes'} en este segmento
            </div>

            {segmentModal.type === 'properties' ? (
              segmentModal.items.length > 0 ? (
                <table className={styles.segmentModalTable}>
                  <thead>
                    <tr>
                      <th>ID</th>
                      <th>Título</th>
                      <th>Fecha cierre</th>
                      <th>Precio cierre</th>
                      <th>Moneda</th>
                      <th>Agente</th>
                    </tr>
                  </thead>
                  <tbody>
                    {segmentModal.items.map(p => (
                      <tr key={p.id}>
                        <td>{p.id}</td>
                        <td>{(p.titulo || p.title || '').slice(0, 35)}</td>
                        <td>{formatDate(p.closedAt || p.fechaCierre)}</td>
                        <td style={{ fontWeight: 600 }}>{formatCurrency(p.precioCierre, p.currencyCierre)}</td>
                        <td>{p.currencyCierre || '—'}</td>
                        <td>{p.sellerName || '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <div className={styles.segmentModalEmpty}>No hay propiedades en esta categoría</div>
              )
            ) : (
              segmentModal.items.length > 0 ? (
                <table className={styles.segmentModalTable}>
                  <thead>
                    <tr>
                      <th>ID</th>
                      <th>Agente</th>
                      <th>Vendidas</th>
                      <th>Agencia</th>
                      <th>Sin clasif.</th>
                      <th>Monto total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {segmentModal.items.map(s => (
                      <tr key={s.sellerId}>
                        <td>{s.sellerId}</td>
                        <td style={{ fontWeight: 500 }}>{s.fullName || `${s.name || ''} ${s.lastName || ''}`}</td>
                        <td style={{ fontWeight: 700 }}>{s.totalSoldProperties || 0}</td>
                        <td>{s.soldByAgencyCount || 0}</td>
                        <td>{s.unclassifiedClosedCount || 0}</td>
                        <td>{formatCurrency(s.totalSaleAmount)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <div className={styles.segmentModalEmpty}>No hay agentes en esta categoría</div>
              )
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default ProductividadStats;
